/**
 * Analytics service for dashboard metrics and reporting
 */

import api from './api';

export interface DashboardSummary {
  total_conversations_today: number;
  total_conversations_change: number;
  active_conversations: number;
  active_conversations_change: number;
  total_leads_today: number;
  total_leads_change: number;
  conversion_rate_today: number;
  conversion_rate_change: number;
  avg_response_time: number;
  avg_response_time_change: number;
  user_satisfaction: number;
  user_satisfaction_change: number;
}

export interface RealtimeSystemStatus {
  status: string;
  active_conversations: number;
  new_conversations_today?: number;
  last_updated?: string;
  database_connected?: boolean;
  websocket_connections?: number;
}

export interface RealtimeDashboardResponse {
  dashboard_summary: DashboardSummary;
  system_status: RealtimeSystemStatus;
  timestamp?: string;
}

export interface DashboardMetric {
  id: number;
  date: string;
  total_conversations: number;
  new_conversations: number;
  active_conversations: number;
  completed_conversations: number;
  total_messages: number;
  user_messages: number;
  bot_messages: number;
  avg_response_time: number;
  avg_conversation_duration: number;
  unique_users: number;
  returning_users: number;
  created_at: string;
  updated_at: string;
}

export interface LeadAnalytics {
  total_leads: number;
  qualified_leads: number;
  quoted_leads?: number;
  converted_leads?: number;
  conversion_rate: number;
  avg_lead_score: number;
  lead_distribution: Record<string, number>;
  project_performance: Array<Record<string, any>>;
  lead_trends: TrendPoint[];
}

export interface PerformanceMetric {
  id: number;
  date: string;
  avg_bot_response_time: number;
  avg_bot_confidence: number;
  successful_responses: number;
  failed_responses: number;
  escalated_conversations: number;
  user_satisfaction_score: number;
  audio_processing_time: number;
  vector_search_time: number;
  created_at: string;
  updated_at: string;
}

export interface TrendPoint {
  date: string;
  value: number;
  label?: string;
}

export interface AlertMetric {
  id: number;
  alert_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  metric_value?: number;
  threshold_value?: number;
  is_resolved: boolean;
  resolved_at?: string;
  created_at: string;
}

export interface DateRangeFilter {
  start_date: string;
  end_date: string;
}

export interface CsatRatingBucket {
  score: number;
  count: number;
}

export interface CsatFeedbackItem {
  id: number;
  conversation_id?: string | null;
  user_id: string;
  score: number;
  comment?: string | null;
  answered_at: string;
  commented_at?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
}

export interface CsatFeedbackSummary {
  start_date: string;
  end_date: string;
  ratings: CsatRatingBucket[];
  items: CsatFeedbackItem[];
}

export interface ExportRequest {
  export_type: string;
  filters: {
    date_range: DateRangeFilter;
    project_codes?: string[];
  };
  format: string;
  include_metadata: boolean;
}

class AnalyticsService {
  /**
   * Get dashboard summary with key metrics
   */
  async getDashboardSummary(targetDate?: string): Promise<DashboardSummary> {
    const params = targetDate ? { target_date: targetDate } : {};
    const response = await api.get('/analytics/dashboard/summary', { params });
    return response.data;
  }

  /**
   * Get real-time dashboard metrics (includes system_status.active_conversations)
   */
  async getRealtimeDashboard(): Promise<RealtimeDashboardResponse> {
    const response = await api.get('/analytics/realtime/dashboard');
    return response.data;
  }

  /**
   * Get dashboard metrics for a date range
   */
  async getDashboardMetrics(startDate: string, endDate: string): Promise<DashboardMetric[]> {
    const response = await api.get('/analytics/dashboard/metrics', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }

  /**
   * Get lead analytics
   */
  async getLeadAnalytics(
    startDate: string, 
    endDate: string, 
    projectCodes?: string[]
  ): Promise<LeadAnalytics> {
    const params: any = { start_date: startDate, end_date: endDate };
    if (projectCodes && projectCodes.length > 0) {
      params.project_codes = projectCodes;
    }
    
    const response = await api.get('/analytics/leads', { params });
    return response.data;
  }

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(startDate: string, endDate: string): Promise<PerformanceMetric[]> {
    const response = await api.get('/analytics/performance', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }

  /**
   * Get performance trends for a specific metric
   */
  async getPerformanceTrends(
    metricName: string, 
    startDate: string, 
    endDate: string
  ): Promise<TrendPoint[]> {
    const response = await api.get(`/analytics/trends/${metricName}`, {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }

  /**
   * Get active system alerts
   */
  async getActiveAlerts(): Promise<AlertMetric[]> {
    const response = await api.get('/analytics/alerts');
    return response.data;
  }

  /**
   * Get CSAT (1-5) distribution + recent free-text feedback
   */
  async getCsatFeedback(startDate: string, endDate: string, limit: number = 50): Promise<CsatFeedbackSummary> {
    const response = await api.get('/analytics/feedback/csat', {
      params: { start_date: startDate, end_date: endDate, limit }
    });
    return response.data;
  }

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId: number): Promise<AlertMetric> {
    const response = await api.put(`/analytics/alerts/${alertId}/resolve`);
    return response.data;
  }

  /**
   * Calculate metrics for a specific date
   */
  async calculateDailyMetrics(targetDate: string): Promise<any> {
    const response = await api.post(`/analytics/calculate/${targetDate}`);
    return response.data;
  }

  /**
   * Export analytics data
   */
  async exportData(exportRequest: ExportRequest): Promise<any> {
    const response = await api.post('/analytics/export', exportRequest, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Generate a comprehensive report
   */
  async generateReport(reportConfig: any): Promise<any> {
    const response = await api.post('/analytics/reports/generate', reportConfig, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Get available export templates
   */
  async getExportTemplates(): Promise<any> {
    const response = await api.get('/analytics/export/templates');
    return response.data;
  }

  /**
   * Get date range for last N days
   */
  getLastNDaysRange(days: number): DateRangeFilter {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  }

  /**
   * Get date range for current month
   */
  getCurrentMonthRange(): DateRangeFilter {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  }

  /**
   * Format percentage change for display
   */
  formatPercentageChange(value: number): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  /**
   * Get severity color for alerts
   */
  getAlertSeverityColor(severity: string): string {
    switch (severity) {
      case 'critical':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#757575';
    }
  }
}

export const analyticsService = new AnalyticsService();
